import { procurementPricingApi, type PurchaseRateDto, type PurchaseRateSource } from "@/api/endpoints";

/**
 * How a purchase document defaults an item's rate, shared by the PO and direct-PI entry screens.
 *
 * Both screens used to fill the rate from `item.defaultMrp` — the retail price the patient pays,
 * not what the vendor charges. Left as typed it flows PO rate → GRN unit cost → `Batch.CostPrice`,
 * so stock ends up valued at retail and every sale of it shows zero margin. The server resolves
 * the real precedence (vendor-item rate → this vendor's last PO → any vendor's last PO → MRP);
 * this module is the client half, so the two screens cannot describe it differently.
 */

/** Short label for the rate's origin, shown beneath the rate field. */
export function purchaseRateLabel(r: PurchaseRateDto): string {
  switch (r.source) {
    case "VendorRate":
      return "Vendor rate";
    case "VendorHistory":
      return r.asOf ? `Last PO ${shortDate(r.asOf)}` : "Last PO";
    case "ItemHistory":
      return r.vendorName
        ? `Last PO — ${r.vendorName}${r.asOf ? ` ${shortDate(r.asOf)}` : ""}`
        : "Last PO — other vendor";
    case "ItemDefaultMrp":
      return "MRP — check";
    default:
      return "No rate";
  }
}

/**
 * Longer wording for the field's tooltip. MRP gets the explanation rather than the label,
 * because the label sits under every line and the reason only matters on the one that fell
 * through to it.
 */
export function purchaseRateHint(r: PurchaseRateDto): string {
  switch (r.source) {
    case "VendorRate":
      return `Rate maintained for ${r.vendorName ?? "this vendor"} on the vendor-items master.`;
    case "VendorHistory":
      return `What ${r.vendorName ?? "this vendor"} last charged for this item.`;
    case "ItemHistory":
      return "This vendor has not supplied this item before — showing the most recent purchase "
        + "from another vendor. Confirm it against the quote.";
    case "ItemDefaultMrp":
      return "No purchase history for this item, so this is its retail MRP — not a purchase rate. "
        + "Replace it with what the vendor charges, or stock will be valued at its selling price.";
    default:
      return "Nothing priced this item. Enter the vendor's rate.";
  }
}

/** Whether the origin warrants visible warning styling rather than a quiet caption. */
export function isPurchaseRateUnreliable(source: PurchaseRateSource): boolean {
  return source === "ItemDefaultMrp" || source === "None";
}

/**
 * Resolve the default rate for a line, falling back to the item's MRP if the lookup fails.
 * A failed lookup must never block typing a purchase document, so the caller gets the same
 * behaviour as before this existed rather than an error.
 */
export async function resolvePurchaseRate(
  itemId: string,
  vendorId: string | undefined,
  fallbackMrp: number | undefined | null,
): Promise<PurchaseRateDto> {
  try {
    return await procurementPricingApi.purchaseRate({ itemId, vendorId: vendorId || undefined });
  } catch {
    return { itemId, rate: fallbackMrp ?? 0, source: fallbackMrp ? "ItemDefaultMrp" : "None" };
  }
}

function shortDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });
}
